import React from 'react';
import {Alert, Empty, Image, Spin, Timeline} from 'antd';
import {useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {
  ComplaintTaskDetailResponse,
  GET_COMPLAINT_TASK_DETAIL,
} from '../../../../graphql/complaintTask/queries';
import {ComplaintTaskDetail, ComplaintTaskLog} from '../../../../graphql/complaintTask/types';
import {translateGraphQLError} from '../../../../utils/graphqlUtils';
import {formatDateTime, formatDuration, formatTime, priorityLabel, roleLabel, subTypeLabel} from '../complaintTaskUtils';
import dayjs from 'dayjs';

/** 四段进度：提报 → 分发 → 响应 → 解决（设计文档 §5.1）。 */
export const ProgressBar: React.FC<{detail: ComplaintTaskDetail}> = ({detail}) => {
  const {t} = useTranslation();
  const {task, serverNow} = detail;
  const now = serverNow ?? new Date().toISOString();
  const diff = (a?: string | null, b?: string | null) => dayjs(a ?? now).valueOf() - dayjs(b).valueOf();

  // 段颜色：已完成 → 绿 / 超时红；进行中 → 未响应黄、未解决红（原型）
  type Tone = 'done' | 'overdue' | 'waiting' | 'responding' | 'resolving';
  const dispatchSeg = task.assignedAt
    ? {text: formatDuration(diff(task.assignedAt, task.startAt)), tone: 'done' as Tone, overdue: false}
    : {text: t('complaintTask.progress.pendingWait', {duration: formatDuration(diff(now, task.startAt))}),
       tone: 'waiting' as Tone, overdue: false};
  const respondOverdue = !!task.responseDueAt && dayjs(task.actualStartAt ?? now).isAfter(task.responseDueAt);
  const respondSeg = !task.assignedAt ? null : task.actualStartAt
    ? {text: formatDuration(diff(task.actualStartAt, task.assignedAt)),
       tone: (respondOverdue ? 'overdue' : 'done') as Tone, overdue: respondOverdue}
    : {text: t('complaintTask.progress.notResponded', {duration: formatDuration(diff(now, task.assignedAt))}),
       tone: 'responding' as Tone, overdue: respondOverdue};
  const resolveOverdue = !!task.resolveDueAt && dayjs(task.finishAt ?? now).isAfter(task.resolveDueAt);
  const resolveSeg = !task.assignedAt ? null : task.finishAt
    ? {text: formatDuration(diff(task.finishAt, task.assignedAt)),
       tone: (resolveOverdue ? 'overdue' : 'done') as Tone, overdue: resolveOverdue}
    : {text: t('complaintTask.progress.notResolved', {duration: formatDuration(diff(now, task.assignedAt))}),
       tone: 'resolving' as Tone, overdue: resolveOverdue};

  const TEXT: Record<Tone, string> = {
    done: 'text-gray-500', overdue: 'text-red-500', waiting: 'text-gray-500', responding: 'text-amber-500', resolving: 'text-red-500',
  };
  const BAR: Record<Tone, string> = {
    done: 'bg-green-400', overdue: 'bg-red-400', waiting: 'bg-gray-300', responding: 'bg-amber-400', resolving: 'bg-red-400',
  };
  const node = (label: string, value: React.ReactNode) => (
    <div className="min-w-[120px]">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
  const seg = (s: {text: string; tone: Tone; overdue: boolean} | null) => (
    <div className="flex-1 px-3 text-center">
      <div className={`text-xs ${s ? TEXT[s.tone] : 'text-gray-500'}`}>
        {s ? s.text : '—'}{s?.overdue ? ` ${t('complaintTask.progress.overdue')}` : ''}
      </div>
      <div className={`h-1 rounded ${s ? BAR[s.tone] : 'bg-gray-200'}`}/>
    </div>
  );
  // 未完成的环节显示时限；无时限（不考核）显示 —
  const due = (iso?: string | null) => (iso ? t('complaintTask.progress.due', {time: formatDateTime(iso)}) : '—');
  const label = (name: string, state: string) => `${t(`complaintTask.progress.${name}`)} · ${t(`complaintTask.progress.${state}`)}`;
  const dispatchState = !task.assignedAt ? 'stateWaiting' : task.assignMode === 2 ? 'stateManual' : 'stateAuto';

  return (
    <div className="flex items-center border rounded-lg p-4 bg-white">
      {node(t('complaintTask.progress.report'), formatDateTime(task.startAt))}
      {seg(dispatchSeg)}
      {node(label('dispatch', dispatchState), task.assignedAt
        ? `${formatDateTime(task.assignedAt)} → ${task.assigneeName ?? ''}`
        : t('complaintTask.status.20'))}
      {seg(respondSeg)}
      {node(label('respond', task.actualStartAt ? 'stateResponded' : 'stateNotResponded'),
        task.actualStartAt ? formatDateTime(task.actualStartAt) : due(task.responseDueAt))}
      {seg(resolveSeg)}
      {node(label('resolve', task.finishAt ? 'stateResolved' : 'stateNotResolved'),
        task.finishAt ? formatDateTime(task.finishAt) : due(task.resolveDueAt))}
    </div>
  );
};

const parseExtra = (json?: string | null): Record<string, any> => {
  if (!json) return {};
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
};

const personName = (label?: string | null) => {
  if (!label) return '';
  const i = label.indexOf(':');
  return i >= 0 ? label.substring(i + 1) : label;
};

/** 流转记录文案（设计文档 §5.4）。 */
export const useLogText = () => {
  const {t} = useTranslation();
  return (log: ComplaintTaskLog): string => {
    const extra = parseExtra(log.extraJson);
    const operator = log.operatorType === 1 ? t('complaintTask.log.system') : (log.operatorName ?? '');
    switch (log.action) {
      case 'CREATE':
        return t('complaintTask.log.create', {chat: extra.chatName ?? '—', priority: priorityLabel(extra.priority)});
      case 'ASSIGN':
        return log.operatorType === 1
          ? t('complaintTask.log.autoAssign', {
            subType: subTypeLabel(extra.subType), role: roleLabel(extra.dispatchRole),
            assignee: personName(log.toValue), assigneeRole: roleLabel(extra.assigneeRole),
          }) + (extra.fallbackToCs ? t('complaintTask.log.fallbackToCs') : '')
          : t('complaintTask.log.manualAssign', {operator, assignee: personName(log.toValue)});
      case 'TRANSFER':
        return t('complaintTask.log.transfer', {operator, from: personName(log.fromValue), to: personName(log.toValue)})
          + (log.reason ? `（${log.reason}）` : '');
      case 'UPDATE':
        return t('complaintTask.log.update');
      case 'LEVEL_CHANGE':
        return t('complaintTask.log.levelChange', {from: priorityLabel(Number(log.fromValue)), to: priorityLabel(Number(log.toValue))});
      case 'RESPOND':
        return t('complaintTask.log.respond', {operator});
      case 'RESOLVE':
        return t('complaintTask.log.resolve', {operator});
      case 'MSG_APPEND':
        return t('complaintTask.log.msgAppend');
      case 'DELETE':
        return t('complaintTask.log.delete');
      default:
        return log.action ?? '';
    }
  };
};

/** 客户原话里的图片链接（企微图片消息转成的 OSS 地址），按后缀识别 */
const IMAGE_URL = /https?:\/\/[^\s"'<>]+?\.(?:jpe?g|png|gif|webp|bmp)(?:\?[^\s"'<>]*)?/gi;
/** 图片加载失败时的灰色占位 */
const IMAGE_FALLBACK = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96"><rect width="96" height="96" fill="%23f3f4f6"/></svg>';

/** 一条原话：图片链接从正文里拿出来，在下方显示缩略图（点开看大图）；PC 详情与手机版共用 */
export const MessageContent: React.FC<{content?: string | null}> = ({content}) => {
  const text = content ?? '';
  const images = Array.from(new Set(text.match(IMAGE_URL) ?? []));
  const rest = images.length ? text.replace(IMAGE_URL, '').trim() : text;
  return (
    <>
      <span className="whitespace-pre-wrap break-words">{rest}</span>
      {images.length > 0 && (
        <Image.PreviewGroup>
          <div className="flex flex-wrap gap-2 mt-1">
            {images.map((src) => (
              <Image key={src} src={src} width={96} height={96} className="object-cover rounded" fallback={IMAGE_FALLBACK}/>
            ))}
          </div>
        </Image.PreviewGroup>
      )}
    </>
  );
};

export const MessageList: React.FC<{detail: ComplaintTaskDetail}> = ({detail}) => {
  const {t} = useTranslation();
  return (
    <div>
      <div className="text-sm text-gray-500 mb-2">
        {t('complaintTask.detail.messagesTitle', {chat: detail.task.chatName ?? '—', count: detail.messages.length})}
      </div>
      {detail.messages.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}/> : (
        // 每条之间留空行，避免长消息挤在一起
        <div className="bg-gray-50 rounded p-3 max-h-96 overflow-auto text-sm leading-6 flex flex-col gap-3">
          {detail.messages.map((m, i) => (
            <div key={i}>
              <span className="text-gray-400">[{m.sentAt ? dayjs(m.sentAt).format('YYYY-MM-DD HH:mm:ss') : '—'}]</span>{' '}
              <span className="text-purple-600">{m.speaker}</span>：<MessageContent content={m.content}/>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ResolutionView: React.FC<{detail: ComplaintTaskDetail}> = ({detail}) => {
  const {t} = useTranslation();
  if (!detail.resolutionContent) return null;
  return (
    <div className="border rounded p-3 bg-green-50">
      <div className="text-sm text-gray-500 mb-1">
        {t('complaintTask.detail.resolution', {by: detail.resolvedBy ?? '', at: formatTime(detail.resolvedAt)})}
      </div>
      <div className="whitespace-pre-wrap">{detail.resolutionContent}</div>
      {detail.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {detail.attachments.map((a) => a.contentType?.startsWith('image/') && a.url
            ? <Image key={a.id} src={a.url} width={96} height={96} className="object-cover"/>
            : a.contentType?.startsWith('video/') && a.url
              ? <video key={a.id} src={a.url} controls className="w-60"/>
              : <a key={a.id} href={a.url ?? undefined} target="_blank" rel="noreferrer">{a.fileName}</a>)}
        </div>
      )}
    </div>
  );
};

/** 管理端行内展开：进度 + 客户原话 + 流转记录 + 处理结果。 */
const ComplaintTaskDetailPanel: React.FC<{taskId: number}> = ({taskId}) => {
  const {t} = useTranslation();
  const logText = useLogText();
  const {data, loading, error} = useQuery<ComplaintTaskDetailResponse>(GET_COMPLAINT_TASK_DETAIL, {
    variables: {taskId},
    fetchPolicy: 'network-only',
  });
  const detail = data?.complaintTaskDetail;
  if (error) return <Alert type="error" showIcon message={translateGraphQLError(error, 'complaintTask.errors.loadFailed')}/>;
  return (
    <Spin spinning={loading}>
      {detail && (
        <div className="flex flex-col gap-4 p-2">
          <div>
            <div className="text-sm font-medium mb-2">{t('complaintTask.detail.progressTitle')}</div>
            <ProgressBar detail={detail}/>
          </div>
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 flex flex-col gap-3">
              <MessageList detail={detail}/>
              <ResolutionView detail={detail}/>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-2">{t('complaintTask.detail.logsTitle')}</div>
              <Timeline
                items={detail.logs.map((l, i) => ({
                  key: i,
                  children: (
                    <div className="text-xs">
                      {/* 建单记录显示提报时间（客户提出时刻），其余为操作发生时间 */}
                      <span className="text-gray-400 mr-2">
                        {formatTime(l.action === 'CREATE' ? detail.task.startAt ?? l.occurredAt : l.occurredAt)}
                      </span>
                      {logText(l)}
                    </div>
                  ),
                }))}
              />
            </div>
          </div>
        </div>
      )}
    </Spin>
  );
};

export default ComplaintTaskDetailPanel;
